/**
 * AssignedOversightPage — Read-only oversight of all assigned leads.
 *
 * Apr 28, 2026 (Squad-D). Screen 06 of 12.
 *
 * Shows all leads currently assigned to teams across the org, with:
 *  - team / executive / stage / aging / last activity
 *  - "Request reassignment" CTA on each row → opens reason modal,
 *    POSTs to /leads/:id/request-reassign
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Download } from 'lucide-react';
import { api } from '@/utils/api';
import { safeGet } from '@/pages/dashboard/roles/_dashboardShared';
import {
  PageShell,
  EmptyState,
  Avatar,
  type Lead,
  unwrapList,
  leadName,
  leadProduct,
  relativeTime,
} from './_lmShared';

interface AssignedLead extends Lead {
  stage?: string | null;
  lastActivity?: string | null;
  lastActivityAt?: string | null;
  agingDays?: number | null;
}

export function AssignedOversightPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AssignedLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reassignTarget, setReassignTarget] = useState<AssignedLead | null>(null);

  useEffect(() => {
    setLoading(true);
    // Apr 28 evening — `scope=assigned` resolves to assignedToId IS NOT NULL.
    safeGet<unknown>('/lead-integrations/lead-imports?scope=assigned&limit=200').then((r) => {
      const raw = (r.data as any)?.imports ?? r.data;
      setRows(unwrapList<AssignedLead>(raw));
      setError(r.error);
      setLoading(false);
    });
  }, []);

  const stats = useMemo(() => {
    const byTeam = new Set<string>();
    rows.forEach((r) => r.teamName && byTeam.add(r.teamName));
    return { count: rows.length, teams: byTeam.size };
  }, [rows]);

  return (
    <PageShell
      title="Assigned leads"
      sub={
        loading
          ? 'Loading…'
          : `Read-only oversight · ${stats.count} active across ${stats.teams} team${
              stats.teams === 1 ? '' : 's'
            }`
      }
      right={
        <>
          <button className="btn btn-outline">
            By team <ChevronDown size={12} />
          </button>
          <button className="btn btn-outline">
            By aging <ChevronDown size={12} />
          </button>
          <button className="btn btn-outline">
            <Download size={12} /> Export
          </button>
        </>
      }
      scroll={false}
    >
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {loading ? (
          <EmptyState title="Loading…" />
        ) : error ? (
          <EmptyState
            title="Assigned-leads view not available yet"
            sub="Backend endpoint isn't online. Once /leads?status=assigned ships, all assigned leads appear here."
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No assigned leads"
            sub="Once you assign verified leads to teams, they appear here."
          />
        ) : (
          <table className="tbl" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Team</th>
                <th>Executive</th>
                <th>Stage</th>
                <th>Aging</th>
                <th>Last activity</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const aging = a.agingDays ?? 0;
                const agingTone =
                  aging >= 6 ? 'var(--danger)' : aging >= 3 ? 'var(--warn)' : 'var(--ink-1)';
                return (
                  <tr key={a.id}>
                    <td>
                      <div
                        style={{ fontWeight: 500, cursor: 'pointer' }}
                        onClick={() => navigate(`/lead-manager/leads/${a.id}`)}
                      >
                        {leadName(a)}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                        <span className="mono">{a.id.slice(-6)}</span> · {leadProduct(a)}
                      </div>
                    </td>
                    <td>{a.teamName ?? '—'}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Avatar name={a.assigneeName ?? '?'} size={20} />
                        <span>{a.assigneeName ?? '—'}</span>
                      </div>
                    </td>
                    <td>
                      <span
                        className={`chip ${
                          a.stage === 'Won'
                            ? 'chip-green'
                            : a.stage === 'Quoted'
                            ? 'chip-amber'
                            : ''
                        }`}
                      >
                        {a.stage ?? '—'}
                      </span>
                    </td>
                    <td>
                      <span className="mono" style={{ fontSize: 12, color: agingTone }}>
                        {aging > 0 ? `${aging} d` : '—'}
                      </span>
                    </td>
                    <td style={{ fontSize: 12 }}>
                      <div>{a.lastActivity ?? '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                        {a.lastActivityAt ? relativeTime(a.lastActivityAt) + ' ago' : '—'}
                      </div>
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => setReassignTarget(a)}
                      >
                        Request reassignment
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {reassignTarget && (
        <ReassignmentRequestModal
          lead={reassignTarget}
          onClose={() => setReassignTarget(null)}
          onSubmitted={() => {
            setReassignTarget(null);
            navigate('/lead-manager/reassignments');
          }}
        />
      )}
    </PageShell>
  );
}

function ReassignmentRequestModal({
  lead,
  onClose,
  onSubmitted,
}: {
  lead: AssignedLead;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [reason, setReason] = useState('');
  const [toUserId, setToUserId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!reason.trim()) {
      alert('Please add a reason — admin needs context to approve the request.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/leads/${lead.id}/request-reassign`, {
        toUserId: toUserId || undefined,
        reason,
      });
      onSubmitted();
    } catch (err) {
      alert('Submission failed — backend not yet ready, or permission denied.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(27,23,19,.42)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="aura-root"
        style={{
          width: 480,
          maxWidth: '100%',
          background: 'var(--ivory-0)',
          borderRadius: 14,
          boxShadow: 'var(--sh-4)',
          border: '1px solid var(--line)',
          padding: 18,
        }}
      >
        <div style={{ fontFamily: 'var(--ff-serif)', fontWeight: 500, fontSize: 18, marginBottom: 4 }}>
          Request reassignment
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 14 }}>
          {leadName(lead)} · currently with {lead.assigneeName ?? '—'} ({lead.teamName ?? '—'})
        </div>

        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.06,
              fontWeight: 500,
              marginBottom: 6,
            }}
          >
            Move to (optional)
          </div>
          <input
            value={toUserId}
            onChange={(e) => setToUserId(e.target.value)}
            placeholder="User ID (admin will pick if blank)"
            style={{
              width: '100%',
              height: 32,
              padding: '0 10px',
              border: '1px solid var(--line)',
              borderRadius: 6,
              fontFamily: 'inherit',
              fontSize: 13,
              background: 'var(--ivory-1)',
              outline: 'none',
            }}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.06,
              fontWeight: 500,
              marginBottom: 6,
            }}
          >
            Reason
          </div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Customer relocated to Mysuru — Pradeep covers that region."
            style={{
              width: '100%',
              minHeight: 90,
              padding: '8px 10px',
              border: '1px solid var(--line)',
              borderRadius: 6,
              fontFamily: 'inherit',
              fontSize: 13,
              background: 'var(--ivory-0)',
              outline: 'none',
              resize: 'vertical',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={submit} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Send request'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AssignedOversightPage;
